// What a page needs to know when it is shown in the panel beside the chat:
// that the one chat is already on screen (so it must not mount its own), and
// a way to lend that chat its actions while it is open (the Resume page's
// import and version history).
import { createContext, useContext, useEffect, useRef } from 'react';

export const ShellContext = createContext({ inShell: false, panel: null, setChatActions: () => {} });

export const useShell = () => useContext(ShellContext);

/**
 * Lends the chat a page's actions while the page is open in the panel, e.g.
 * { onImportResume, onOpenVersionHistory }. Registered once; the latest
 * handlers are always the ones called, so a page can pass fresh arrows on
 * every render without the chat re-rendering for it.
 */
export function useShellChatActions(actions) {
  const { inShell, setChatActions } = useShell();
  const latest = useRef(actions);
  latest.current = actions;
  useEffect(() => {
    if (!inShell) return undefined;
    const names = Object.keys(latest.current || {}).filter(k => typeof latest.current[k] === 'function');
    setChatActions(Object.fromEntries(names.map(k => [k, (...args) => latest.current?.[k]?.(...args)])));
    return () => setChatActions({});
  }, [inShell, setChatActions]);
}
